import { api } from './api';
import type { LoginResponse, UserProfile } from '@dpfs/shared';

export interface RegisterInput {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  role: 'DOCTOR' | 'PHARMACIST' | 'PATIENT';
  licenseNumber?: string;
  phoneNumber?: string;
}

export interface LoginInput {
  email: string;
  password: string;
}

export interface MfaValidateInput {
  tempToken: string;
  code: string;
}

export const authService = {
  async register(input: RegisterInput): Promise<{ user: { id: string; email: string } }> {
    const response = await api.post('/auth/register', input);
    return response.data;
  },

  async login(input: LoginInput): Promise<LoginResponse> {
    const response = await api.post('/auth/login', input);
    return response.data;
  },

  async validateMfa(input: MfaValidateInput): Promise<LoginResponse> {
    const response = await api.post('/auth/mfa/validate', input);
    return response.data;
  },

  async setupMfa(): Promise<{ secret: string; qrCode: string }> {
    const response = await api.post('/auth/mfa/setup');
    return response.data;
  },

  async verifyMfa(code: string): Promise<{ success: boolean }> {
    const response = await api.post('/auth/mfa/verify', { code });
    return response.data;
  },

  async refreshToken(refreshToken: string): Promise<{
    accessToken: string;
    refreshToken: string;
    expiresIn: number;
  }> {
    const response = await api.post('/auth/refresh', { refreshToken });
    return response.data;
  },

  async logout(): Promise<void> {
    await api.post('/auth/logout');
  },

  async getCurrentUser(): Promise<UserProfile> {
    const response = await api.get('/auth/me');
    return response.data;
  },

  async getCurrentUserWithToken(token: string): Promise<UserProfile> {
    const response = await api.get('/auth/me', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  },

  getOAuthUrl(provider: 'google' | 'microsoft'): string {
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';
    return `${apiUrl}/auth/oauth/${provider}`;
  },
};
